/**
 * FCRProcessModal - Process a Forwarded Customer Request
 *
 * Modal for processing FCRs:
 * - Shows parsed email content (read-only)
 * - Company selection (pre-selected if matched)
 * - Contact selection or creation
 * - Ticket details (title, description editable)
 * - Process/Ignore/Cancel actions
 */

import { useState, useEffect, useRef } from 'react';
import { ForwardedRequest, Contact, MatchConfidence, Resource, FCREmail, FCREmailAttachment } from '../services/types';
import {
  getFCR,
  getFCREmail,
  processFCR,
  ignoreFCR,
  deleteFCR,
  getActiveCompanies,
  getCompanyContacts,
  getResources,
  CompanyOption,
} from '../services/api';

interface FCRProcessModalProps {
  isOpen: boolean;
  fcr: ForwardedRequest | null;
  onClose: () => void;
  onProcessed?: (ticketNumber: string) => void;
}

function getConfidenceLabel(confidence: MatchConfidence): string {
  switch (confidence) {
    case 'high': return 'High confidence match';
    case 'medium': return 'Medium confidence match';
    case 'low': return 'Low confidence match';
    case 'none': return 'No company match found';
  }
}

function getConfidenceColor(confidence: MatchConfidence): string {
  switch (confidence) {
    case 'high': return 'text-green-400';
    case 'medium': return 'text-yellow-400';
    case 'low': return 'text-orange-400';
    case 'none': return 'text-red-400';
  }
}

export default function FCRProcessModal({ isOpen, fcr: initialFcr, onClose, onProcessed }: FCRProcessModalProps) {
  // FCR data
  const [fcr, setFcr] = useState<ForwardedRequest | null>(null);

  // Form state
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [createNewContact, setCreateNewContact] = useState(false);
  const [newContactFirstName, setNewContactFirstName] = useState('');
  const [newContactLastName, setNewContactLastName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [priority, setPriority] = useState<number>(3); // Default to Medium
  const [assignedResourceId, setAssignedResourceId] = useState<number | null>(null);

  // Data state
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  // Email viewer state
  const [showOriginalEmail, setShowOriginalEmail] = useState(false);
  const [originalEmail, setOriginalEmail] = useState<FCREmail | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingFcr, setLoadingFcr] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const companyInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load FCR details and companies when modal opens
  useEffect(() => {
    if (isOpen && initialFcr) {
      loadFcrAndData();
    }
  }, [isOpen, initialFcr?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCompanyDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load contacts when company changes
  useEffect(() => {
    if (companyId) {
      loadContacts(companyId);
    } else {
      setContacts([]);
      setSelectedContactId(null);
    }
  }, [companyId]);

  async function loadFcrAndData() {
    if (!initialFcr) return;

    setLoadingFcr(true);
    setError(null);

    try {
      // Load full FCR details, companies, and resources in parallel
      const [fullFcr, companiesData, resourcesData] = await Promise.all([
        getFCR(initialFcr.id),
        getActiveCompanies(),
        getResources(),
      ]);

      setFcr(fullFcr);
      setCompanies(companiesData.sort((a, b) => a.companyName.localeCompare(b.companyName)));
      setResources(resourcesData.filter(r =>
        r.isActive &&
        r.licenseType !== 3 &&
        r.autoTaskId !== 4 &&
        !(`${r.firstName} ${r.lastName}`.toLowerCase().includes('api'))
      ).sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
      ));

      // Pre-fill form based on FCR data
      if (fullFcr.matchedCompanyId) {
        setCompanyId(fullFcr.matchedCompanyId);
        setCompanySearch(fullFcr.matchedCompanyName || '');
      }

      // Pre-fill title
      setTicketTitle(`Fwd: ${fullFcr.originalSubject || 'Customer Request'}`);

      // Pre-fill new contact form with original sender info
      if (fullFcr.originalFrom.name) {
        const nameParts = fullFcr.originalFrom.name.split(' ');
        setNewContactFirstName(nameParts[0] || '');
        setNewContactLastName(nameParts.slice(1).join(' ') || '');
      }
      setNewContactEmail(fullFcr.originalFrom.email);

      // Load existing contacts if company matched
      if (fullFcr.matchedCompanyId) {
        await loadContacts(fullFcr.matchedCompanyId);
      }
    } catch (err) {
      console.error('Failed to load FCR:', err);
      setError('Failed to load request details');
    } finally {
      setLoadingFcr(false);
    }
  }

  async function loadContacts(compId: number) {
    setLoadingContacts(true);
    try {
      const contactsData = await getCompanyContacts(compId, true);
      setContacts(contactsData);

      // Auto-select contact if email matches
      if (fcr) {
        const matchingContact = contactsData.find(c =>
          c.email.toLowerCase() === fcr.originalFrom.email.toLowerCase()
        );
        if (matchingContact) {
          setSelectedContactId(matchingContact.id);
          setCreateNewContact(false);
        }
      }
    } catch (err) {
      console.error('Failed to load contacts:', err);
      setContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }

  async function handleViewOriginalEmail() {
    if (!fcr) return;

    // If already loaded, just toggle
    if (originalEmail) {
      setShowOriginalEmail(!showOriginalEmail);
      return;
    }

    setLoadingEmail(true);
    setEmailError(null);
    try {
      const email = await getFCREmail(fcr.id);
      setOriginalEmail(email);
      setShowOriginalEmail(true);
    } catch (err) {
      console.error('Failed to fetch original email:', err);
      setEmailError(
        err instanceof Error ? err.message : 'Failed to fetch original email. The message may no longer be in the mailbox.'
      );
    } finally {
      setLoadingEmail(false);
    }
  }

  function downloadAttachment(att: FCREmailAttachment) {
    if (!att.contentBytes) return;
    const byteChars = atob(att.contentBytes);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: att.contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.name;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Filter companies based on search
  const filteredCompanies = companies.filter(c =>
    c.companyName.toLowerCase().includes(companySearch.toLowerCase())
  );

  async function handleProcess() {
    if (!fcr || !companyId) {
      setError('Please select a company');
      return;
    }

    if (!selectedContactId && !createNewContact) {
      setError('Please select a contact or create a new one');
      return;
    }

    if (createNewContact && (!newContactFirstName.trim() || !newContactLastName.trim() || !newContactEmail.trim())) {
      setError('Please fill in contact first name, last name, and email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const processData: Parameters<typeof processFCR>[1] = {
        companyId,
        title: ticketTitle.trim() || undefined,
        description: ticketDescription.trim() || undefined,
        priority,
        assignedResourceId: assignedResourceId || undefined,
      };

      if (createNewContact) {
        processData.createContact = {
          firstName: newContactFirstName.trim(),
          lastName: newContactLastName.trim(),
          email: newContactEmail.trim(),
          phone: newContactPhone.trim() || undefined,
        };
      } else if (selectedContactId) {
        processData.contactId = selectedContactId;
      }

      const result = await processFCR(fcr.id, processData);

      setSuccess(`Ticket ${result.ticketNumber} created!`);

      if (onProcessed) {
        onProcessed(result.ticketNumber);
      }

      // Close after short delay
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error processing FCR:', err);
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  }

  async function handleIgnore() {
    if (!fcr) return;

    setLoading(true);
    setError(null);

    try {
      await ignoreFCR(fcr.id);
      handleClose();
    } catch (err) {
      console.error('Error ignoring FCR:', err);
      setError(err instanceof Error ? err.message : 'Failed to ignore request');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (!fcr) return;

    if (!confirm('Delete this FCR? This will allow the email to be reprocessed if forwarded again.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteFCR(fcr.id);
      handleClose();
      onProcessed?.('deleted');
    } catch (err) {
      console.error('Error deleting FCR:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete request');
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    // Reset state
    setFcr(null);
    setCompanyId(null);
    setSelectedContactId(null);
    setCreateNewContact(false);
    setNewContactFirstName('');
    setNewContactLastName('');
    setNewContactEmail('');
    setNewContactPhone('');
    setTicketTitle('');
    setTicketDescription('');
    setPriority(3);
    setAssignedResourceId(null);
    setCompanySearch('');
    setContacts([]);
    setError(null);
    setSuccess(null);
    setShowOriginalEmail(false);
    setOriginalEmail(null);
    setEmailError(null);
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className={`relative bg-slate-800 border border-slate-700 rounded-xl shadow-2xl w-full mx-4 max-h-[90vh] overflow-hidden transition-all ${showOriginalEmail ? 'max-w-4xl' : 'max-w-2xl'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <svg className="w-5 h-5 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Process Forwarded Request
          </h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loadingFcr ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
          ) : fcr ? (
            <div className="space-y-6">
              {/* Error/Success Messages */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-2 rounded-lg text-sm">
                  {success}
                </div>
              )}

              {/* Original Email Info */}
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-300">Original Message</h3>
                  <button
                    onClick={handleViewOriginalEmail}
                    disabled={loadingEmail}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-violet-600/80 hover:bg-violet-500 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loadingEmail ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )}
                    {showOriginalEmail ? 'Hide Full Email' : 'View Full Email'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-400">From:</span>
                    <span className="text-white ml-2">
                      {fcr.originalFrom.name ? `${fcr.originalFrom.name} <${fcr.originalFrom.email}>` : fcr.originalFrom.email}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400">Forwarded by:</span>
                    <span className="text-white ml-2">{fcr.forwardedBy}</span>
                  </div>
                </div>
                <div className="text-sm">
                  <span className="text-slate-400">Subject:</span>
                  <span className="text-white ml-2">{fcr.originalSubject || '(no subject)'}</span>
                </div>

                {/* Email error */}
                {emailError && (
                  <div className="mt-3 bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded text-xs">
                    {emailError}
                  </div>
                )}

                {/* Full original email viewer */}
                {showOriginalEmail && originalEmail ? (
                  <div className="mt-3 space-y-3">
                    {/* Rendered HTML email */}
                    <div className="bg-white rounded overflow-hidden">
                      <iframe
                        srcDoc={originalEmail.bodyHtml}
                        title="Original email"
                        className="w-full border-0"
                        style={{ minHeight: '300px', maxHeight: '500px' }}
                        sandbox="allow-same-origin"
                        onLoad={(e) => {
                          // Auto-resize iframe to content height
                          const iframe = e.target as HTMLIFrameElement;
                          try {
                            const height = iframe.contentDocument?.body?.scrollHeight || 300;
                            iframe.style.height = Math.min(height + 20, 500) + 'px';
                          } catch {
                            // cross-origin restriction, use default height
                          }
                        }}
                      />
                    </div>

                    {/* Attachments */}
                    {originalEmail.attachments.filter(a => !a.isInline && a.contentBytes).length > 0 && (
                      <div>
                        <span className="text-slate-400 text-xs font-medium">Attachments:</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {originalEmail.attachments
                            .filter(a => !a.isInline && a.contentBytes)
                            .map((att) => (
                              <button
                                key={att.id}
                                onClick={() => downloadAttachment(att)}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-600 hover:bg-slate-500 rounded text-xs text-white transition-colors"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {att.name}
                                <span className="text-slate-400">({(att.size / 1024).toFixed(0)}KB)</span>
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Fallback: parsed text body */
                  fcr.originalBody && !showOriginalEmail && (
                    <div className="mt-3">
                      <span className="text-slate-400 text-sm">Body:</span>
                      <div className="mt-1 bg-slate-800 rounded p-3 text-slate-300 text-sm max-h-32 overflow-y-auto whitespace-pre-wrap">
                        {fcr.originalBody.substring(0, 1000)}
                        {fcr.originalBody.length > 1000 && '...'}
                      </div>
                    </div>
                  )
                )}

                {/* Matching Notes (collapsible) */}
                {fcr.matchingNotes && fcr.matchingNotes.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-slate-400 text-xs cursor-pointer hover:text-slate-300 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Matching debug info ({fcr.matchingNotes.length} steps)
                    </summary>
                    <div className="mt-2 bg-slate-800 rounded p-2 text-xs text-slate-400 font-mono max-h-40 overflow-y-auto">
                      {fcr.matchingNotes.map((note, idx) => (
                        <div key={idx} className={note.includes('MATCHED') ? 'text-green-400' : note.includes('NO MATCH') || note.includes('failed') ? 'text-red-400' : ''}>
                          {note}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>

              {/* Company Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Company *
                  {fcr.matchConfidence !== 'none' && (
                    <span className={`ml-2 text-xs ${getConfidenceColor(fcr.matchConfidence)}`}>
                      ({getConfidenceLabel(fcr.matchConfidence)})
                    </span>
                  )}
                </label>
                <div className="relative" ref={dropdownRef}>
                  <input
                    ref={companyInputRef}
                    type="text"
                    value={companySearch}
                    onChange={(e) => {
                      setCompanySearch(e.target.value);
                      setShowCompanyDropdown(true);
                      if (!e.target.value) setCompanyId(null);
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    placeholder="Search companies..."
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
                  />
                  {showCompanyDropdown && filteredCompanies.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredCompanies.slice(0, 50).map(company => (
                        <button
                          key={company.autoTaskId}
                          type="button"
                          onClick={() => {
                            setCompanyId(company.autoTaskId);
                            setCompanySearch(company.companyName);
                            setShowCompanyDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 hover:bg-slate-600 text-sm ${
                            company.autoTaskId === companyId ? 'bg-slate-600 text-white' : 'text-slate-300'
                          }`}
                        >
                          {company.companyName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Selection */}
              {companyId && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Contact *</label>
                  {loadingContacts ? (
                    <div className="text-slate-400 text-sm">Loading contacts...</div>
                  ) : (
                    <>
                      {/* Existing contacts */}
                      {contacts.length > 0 && (
                        <div className="space-y-1 mb-3">
                          {contacts.map(contact => (
                            <label
                              key={contact.id}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-700 ${
                                selectedContactId === contact.id && !createNewContact ? 'bg-slate-700' : ''
                              }`}
                            >
                              <input
                                type="radio"
                                name="contact"
                                checked={selectedContactId === contact.id && !createNewContact}
                                onChange={() => {
                                  setSelectedContactId(contact.id);
                                  setCreateNewContact(false);
                                }}
                                className="text-violet-500"
                              />
                              <span className="text-white text-sm">
                                {contact.firstName} {contact.lastName}
                              </span>
                              <span className="text-slate-400 text-xs">{contact.email}</span>
                              {contact.email.toLowerCase() === fcr.originalFrom.email.toLowerCase() && (
                                <span className="text-green-400 text-xs">(email match)</span>
                              )}
                            </label>
                          ))}
                        </div>
                      )}

                      {/* Create new contact option */}
                      <label
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-700 ${
                          createNewContact ? 'bg-slate-700' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="contact"
                          checked={createNewContact}
                          onChange={() => {
                            setCreateNewContact(true);
                            setSelectedContactId(null);
                          }}
                          className="text-violet-500"
                        />
                        <span className="text-white text-sm">Create new contact</span>
                      </label>

                      {/* New contact form */}
                      {createNewContact && (
                        <div className="mt-3 ml-6 p-3 bg-slate-700/50 rounded-lg space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">First Name *</label>
                              <input
                                type="text"
                                value={newContactFirstName}
                                onChange={(e) => setNewContactFirstName(e.target.value)}
                                className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1 text-white text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Last Name *</label>
                              <input
                                type="text"
                                value={newContactLastName}
                                onChange={(e) => setNewContactLastName(e.target.value)}
                                className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1 text-white text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Email *</label>
                            <input
                              type="email"
                              value={newContactEmail}
                              onChange={(e) => setNewContactEmail(e.target.value)}
                              className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1 text-white text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Phone</label>
                            <input
                              type="tel"
                              value={newContactPhone}
                              onChange={(e) => setNewContactPhone(e.target.value)}
                              className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1 text-white text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Ticket Details */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Ticket Title</label>
                <input
                  type="text"
                  value={ticketTitle}
                  onChange={(e) => setTicketTitle(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-violet-500"
                  placeholder="Enter ticket title..."
                />
              </div>

              {/* Priority and Assignment */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Priority</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(parseInt(e.target.value, 10))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  >
                    <option value={1}>Critical</option>
                    <option value={2}>High</option>
                    <option value={3}>Medium</option>
                    <option value={4}>Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Assign To</label>
                  <select
                    value={assignedResourceId || ''}
                    onChange={(e) => setAssignedResourceId(e.target.value ? parseInt(e.target.value, 10) : null)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-violet-500"
                  >
                    <option value="">Unassigned</option>
                    {resources.map(r => (
                      <option key={r.autoTaskId} value={r.autoTaskId}>{r.firstName} {r.lastName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Additional Notes
                  <span className="text-slate-400 text-xs ml-2">(optional - original email will be included)</span>
                </label>
                <textarea
                  value={ticketDescription}
                  onChange={(e) => setTicketDescription(e.target.value)}
                  rows={3}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-violet-500 resize-none"
                  placeholder="Add any additional notes..."
                />
              </div>
            </div>
          ) : (
            <div className="text-center text-slate-400 py-8">
              No request selected
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700 bg-slate-800/50">
          <div className="flex gap-2">
            <button
              onClick={handleIgnore}
              disabled={loading || !fcr}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
            >
              Ignore
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || !fcr}
              className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
            >
              Delete
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleProcess}
              disabled={loading || !fcr || !companyId || (!selectedContactId && !createNewContact)}
              className="px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              Create Ticket
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
